import { Box, IconButton } from '@youversion/design-system-components';
import BibleSmallStroked from '@youversion/design-system-icons/lib/BibleSmallStroked';
import CheckmarkStroked from '@youversion/design-system-icons/lib/CheckmarkStroked';
import GlobeStroked from '@youversion/design-system-icons/lib/GlobeStroked';
import XStroked from '@youversion/design-system-icons/lib/XStroked';
import Modal from 'components/ui/modal';
import ModalHeader from 'components/ui/modal/Header';
import { useQueryParams } from 'hooks/useQueryParams';
import { useSelectorEvents } from 'hooks/useSelectorEvents';
import { useTranslation } from 'next-i18next';
import React, { useEffect } from 'react';
import { firebaseLogEvent } from 'utils/firebase';
import { useFund } from 'utils/store/fund';

import GiveOptionListItem from '../controls/GiveOptionListItem';
import RadioListItem from '../controls/RadioListItem';
import ModalFooter from '../modal/Footer';

interface Props {
  onClose: () => void;
}

const icons = {
  5: <GlobeStroked className='dark:text-text-dark text-text-light' />,
  6: <BibleSmallStroked className='dark:text-text-dark text-text-light' />,
};

type IconId = keyof typeof icons;

export default function FundDialog({ onClose }: Props) {
  const { funds, selectedFund, setFund } = useFund();
  const { t } = useTranslation('common');
  const { hide_nav: hideNav } = useQueryParams();
  const { logSelectorDismissedEvent, logSelectorPresentedEvent } = useSelectorEvents('giving_cause');

  const secondaryText: { [key: number]: string } = {
    5: t('giving.titles.bible_translation_giving'),
    6: t('giving.titles.youversion_giving'),
  };

  useEffect(() => {
    firebaseLogEvent('GivingScreenView', { screen: 'giving_cause' });
    logSelectorPresentedEvent(selectedFund);
    return () => void logSelectorDismissedEvent(selectedFund);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal aria-labelledby='dialog_modal_title_frequency' onClose={onClose}>
      <div className='flex flex-col justify-between h-full z-10'>
        <ModalHeader
          left={
            <IconButton className='inline-end-2 block-start-2' data-testid='close-modal-button' onClick={onClose}>
              <XStroked className='text-text-light dark:text-text-dark' />
              <label className='sr-only'>{t('giving.cta.close')}</label>
            </IconButton>
          }
          title={t('giving.titles.fund')}
        />
        {hideNav ? (
          <>
            <Box className='grow overflow-x-auto pli-3'>
              {funds.map(item => {
                const isSelected = selectedFund === item.id;
                return (
                  <GiveOptionListItem
                    className={isSelected ? 'bg-blue-10 dark:bg-gray-30' : ''}
                    id={`radio_fund_${item.id}`}
                    key={item.id}
                    label={item.name}
                    onClick={() => {
                      firebaseLogEvent('GivingButtonTap', { label: 'Cause', screen: 'giving_cause', value: item.name });
                      setFund(item.id);
                    }}
                    rightElement={
                      isSelected ? <CheckmarkStroked className='dark:text-text-dark text-text-light' /> : undefined
                    }
                    secondaryLabel={secondaryText[item.id]}
                    startIcon={icons[item.id as IconId]}
                    value={item.id}
                  />
                );
              })}
            </Box>
            <ModalFooter
              buttonText={t('giving.cta.done')}
              onClick={() => {
                firebaseLogEvent('GivingButtonTap', { label: 'Done', screen: 'giving_cause' });
                onClose();
              }}
            />
          </>
        ) : (
          <>
            <Box className='flex flex-col grow overflow-x-auto pli-2 gap-2'>
              {funds.map(item => {
                const isSelected = selectedFund === item.id;
                return (
                  <RadioListItem
                    id={`radio_fund_${item.id}`}
                    isChecked={isSelected}
                    key={item.id}
                    label={item.name}
                    onChange={() => {
                      firebaseLogEvent('GivingButtonTap', { label: 'Cause', screen: 'giving_cause', value: item.name });
                      setFund(item.id);
                    }}
                    secondaryLabel={secondaryText[item.id as IconId]}
                    startIcon={icons[item.id as IconId]}
                    value={item.id}
                  />
                );
              })}
            </Box>
            <ModalFooter
              buttonText={t('giving.cta.done')}
              onClick={() => {
                firebaseLogEvent('GivingButtonTap', { label: 'Done', screen: 'giving_cause' });
                onClose();
              }}
            />
          </>
        )}
      </div>
    </Modal>
  );
}
